import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SxProps, Theme, FormControlProps } from "@mui/material";
import { MClassEnum, CClassEnum, PClassEnum, MClassLavTargetValuesEnum, CClassEavTargetValuesEnum, PClassEavTargetValuesEnum } from "types";
import { ToggleGroup } from "components/ToggleGroup";

type Props = Omit<FormControlProps, "onChange"> & {
  classType: "M" | "C" | "P";
  name: string;
  selected: MClassEnum | CClassEnum | PClassEnum | string;
  noDesc?: boolean;
  sx?: SxProps<Theme>;
  onChange: (name: string, value: MClassEnum | CClassEnum | PClassEnum) => void;
};

type ClassType = Props["classType"];
type ClassValue = MClassEnum | CClassEnum | PClassEnum;
type ClassOption = {
  label: string;
  value: ClassValue;
  description?: string;
};

const CLASS_CONFIG: Record<ClassType, { options: ClassOption[] }> = {
  M: {
    options: ["M1", "M2", "M3", "M4", "M5", "M6"].map((label) => ({
      label,
      value: MClassEnum[label as keyof typeof MClassEnum],
      description: `(${MClassLavTargetValuesEnum[label as keyof typeof MClassLavTargetValuesEnum]} cd/m²)`,
    })),
  },
  C: {
    options: ["C0", "C1", "C2", "C3", "C4", "C5"].map((label) => ({
      label,
      value: CClassEnum[label as keyof typeof CClassEnum],
      description: `(${CClassEavTargetValuesEnum[label as keyof typeof CClassEavTargetValuesEnum]} lx)`,
    })),
  },
  P: {
    options: ["P1", "P2", "P3", "P4", "P5", "P6"].map((label) => ({
      label,
      value: PClassEnum[label as keyof typeof PClassEnum],
      description: `(${PClassEavTargetValuesEnum[label as keyof typeof PClassEavTargetValuesEnum]} lx)`,
    })),
  },
};

const ClassOptions: FC<Props> = ({ classType, name, selected, noDesc, sx = {}, onChange, ...rest }) => {
  const { t } = useTranslation();

  const options = useMemo(
    () => (noDesc ? CLASS_CONFIG[classType].options.map(({ description, ...option }) => option) : CLASS_CONFIG[classType].options),
    [classType, noDesc]
  );

  const handleChange = useCallback((name: string, value: string) => {
    onChange(name, value as ClassValue);
  }, [onChange]);

  return (
    <ToggleGroup
      radio
      fullWidth
      compact
      name={name}
      label={`${classType}-${t("Classes")}`}
      selected={selected}
      options={options}
      onChange={handleChange}
      sx={sx}
      {...rest}
    />
  );
};

export default ClassOptions;
